import { useEffect, useMemo, useRef } from "react";
import { useSearch, Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { resolveReferralCode, setReferralCookie, isCreditableReferrer, buildJoinAgentHref } from "@/lib/referralCookie";

// Associate-specific recruitment landing page — a shareable "why join
// BizConnect360" splash that any associate can send prospects to
// (?ref=<their repCode>), handing off into the EXISTING registration flow
// (/join-agent) with attribution preserved.
//
// Design/content fidelity (2026-07-21 ask): this is a faithful reproduction
// of the client-supplied mockup (bizconnect360-associates.html) — same
// fonts (Inter + Fraunces), same navy/gold palette, same copy word-for-word,
// same layout/section order, same inline SVG icons, same reveal-on-scroll
// behavior, and the SAME real photos — downloaded from the mockup's own
// image URLs and committed into this repo (client/public/associates/) so
// the page no longer depends on bizconnect360.com's external hosting. The
// only things NOT in the original static mockup are additions this page
// structurally requires: server-validated referrer personalization ("Invited
// by X") and real navigation on the CTAs (the mockup's buttons were dead
// anchors — #start / #).
//
// Referrer handling: the repCode is NEVER trusted from the query string
// directly — it's resolved server-side via rep.getByCode (already hardened
// to return only a public-safe subset for a non-self/non-admin caller, see
// server/routers.ts), and the result must pass isCreditableReferrer
// (status === "active") before this page will personalize itself or forward
// the ref onward. An invalid, disabled, rejected, or nonexistent referrer
// silently falls back to the generic, no-referrer version of the page — it
// never errors, never leaks whether a given code exists, and never forwards
// a bad ref into registration.
//
// Admin-editable content (2026-07-22 ask): the logo, hero headline/subtext,
// and the 6 perk cards' title/description are editable from Admin Portal ->
// Associates Landing Page (trpc.associateLanding.{getContent,updateContent},
// see server/_core/associateLandingContent.ts). Layout, fonts, colors,
// icons, section order, and animations are NOT editable — every DEFAULT_*
// constant below is the fallback used whenever no admin override is saved.

// Icons are positional and fixed by design (admin content editing covers
// text only, per client request to keep layout/design unchanged) — index i
// of DEFAULT_PERKS/an admin override always pairs with PERK_ICONS[i].
const PERK_ICONS = [
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 7.5-2" /></svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="12" r="6" /><circle cx="15" cy="12" r="6" /></svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 13a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.55V19a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 13a1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 11a1.7 1.7 0 0 0-.34-1.87l-.06-.06A2 2 0 1 1 7.03 6.24l.06.06A1.7 1.7 0 0 0 8.96 6.6 1.7 1.7 0 0 0 10 5.09V5a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 11c.16.5.5.9 1 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1z" /></svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M2 9l10-5 10 5-10 5-10-5z" /><path d="M6 11v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" /></svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5" /><circle cx="17" cy="9" r="2.3" /><path d="M15.5 15c2.5.2 4.5 1.8 4.5 5" /></svg>,
];

const DEFAULT_LOGO_URL = "/associates/bizconnect360-logo.png";
const DEFAULT_HERO_HEADLINE = "Build a business that's actually yours.";
const DEFAULT_HERO_SUBTEXT = "Be a part of the BizConnect360 community and get real infrastructure, proven systems, and a team behind you — so you're building something lasting, never alone.";

const DEFAULT_PERKS = [
  {
    title: "Zero Barrier To Entry",
    description: "100% free to start. No licensing, no fine print, no upfront cost — just a real opportunity, open and waiting for you to take it.",
  },
  {
    title: "Relationships That Compound",
    description: "An ecosystem built around trusted relationships and meaningful introductions — you'll never spend time prospecting again.",
  },
  {
    title: "Real Technology & Systems",
    description: "A dedicated portal, ready-to-use materials, and tools built to support you — not busywork bolted on after the fact.",
  },
  {
    title: "Training & Development",
    description: "Live coaching and ongoing education from people who've actually built this — not a PDF you're left to figure out alone.",
  },
  {
    title: "Unlimited Income Potential",
    description: "There's no ceiling here. What you build — and what it's worth — is entirely up to you.",
  },
  {
    title: "A Community, Not A Competition",
    description: "Join a network of like-minded people building alongside you — sharing what works instead of guarding it.",
  },
];

// Scoped CSS, ported 1:1 from bizconnect360-associates.html's <style> block
// (selectors prefixed with .bc-assoc so nothing leaks into the rest of the
// app, which doesn't use scoped stylesheets elsewhere). Colors, spacing,
// type sizes, and transitions are unchanged from the source file.
const STYLES = `
.bc-assoc{--navy:#0c1f3a;--navy-deep:#081527;--gold:#c9a24a;--gold-deep:#b08a3b;--gray:#5c6675;--border:#e3e6ec;--light:#f7f8fa;
  font-family:'Inter',Arial,sans-serif;color:var(--navy);line-height:1.7;background:#fff;overflow-x:hidden;}
.bc-assoc *{box-sizing:border-box;}
.bc-assoc a{text-decoration:none;color:inherit;}
.bc-assoc img{max-width:100%;display:block;}
.bc-assoc h1,.bc-assoc h2,.bc-assoc h3,.bc-assoc .serif{font-family:'Fraunces',serif;}
.bc-assoc :focus-visible{outline:2px solid var(--gold);outline-offset:2px;}

.bc-assoc .reveal{opacity:0;transform:translateY(24px);transition:opacity .8s ease, transform .8s ease;}
.bc-assoc .reveal.show{opacity:1;transform:translateY(0);}

.bc-assoc header{position:absolute;top:0;left:0;right:0;z-index:20;padding:30px 6%;display:flex;justify-content:space-between;align-items:center;}
.bc-assoc header img.logo{height:30px;}
.bc-assoc .btn{display:inline-block;background:var(--gold);color:var(--navy-deep);padding:16px 40px;font-weight:700;font-size:.98rem;border-radius:4px;letter-spacing:.2px;box-shadow:0 14px 30px rgba(201,162,74,.3);transition:transform .2s ease, box-shadow .2s ease;border:none;cursor:pointer;}
.bc-assoc .btn:hover{transform:translateY(-2px);box-shadow:0 18px 36px rgba(201,162,74,.4);}
.bc-assoc .btn-ghost{border:1.5px solid rgba(255,255,255,.6);color:#fff;padding:12px 26px;font-weight:600;font-size:.88rem;border-radius:4px;transition:background .2s ease;}
.bc-assoc .btn-ghost:hover{background:rgba(255,255,255,.12);}

.bc-assoc .hero{position:relative;min-height:100vh;display:flex;align-items:center;color:#fff;overflow:hidden;}
.bc-assoc .hero-bg{position:absolute;inset:0;background:url('/associates/hero-team-meeting.jpg') center 32%/cover;transform:scale(1.05);}
.bc-assoc .hero-scrim{position:absolute;inset:0;background:linear-gradient(100deg,rgba(8,21,39,.96) 25%,rgba(8,21,39,.75) 60%,rgba(8,21,39,.6) 100%);}
.bc-assoc .hero-inner{position:relative;z-index:1;max-width:1200px;margin:0 auto;padding:0 6%;width:100%;}
.bc-assoc .hero-inner .content{max-width:620px;}
.bc-assoc .eyebrow{display:inline-flex;align-items:center;gap:10px;color:var(--gold);letter-spacing:2.5px;font-size:.78rem;font-weight:700;text-transform:uppercase;margin-bottom:24px;}
.bc-assoc .eyebrow::before{content:"";width:24px;height:1px;background:var(--gold);}
.bc-assoc .invited-by{color:#d3dcec;font-size:.9rem;font-weight:600;margin-bottom:14px;}
.bc-assoc .invited-by b{color:var(--gold);}
.bc-assoc .hero h1{font-size:3.3rem;font-weight:600;line-height:1.16;margin-bottom:24px;}
.bc-assoc .hero p{font-size:1.16rem;color:#d3dcec;max-width:540px;margin-bottom:38px;}

.bc-assoc .scroll-cue{position:absolute;bottom:30px;left:50%;transform:translateX(-50%);z-index:1;color:#9fb0c9;font-size:.7rem;letter-spacing:2px;text-transform:uppercase;}
.bc-assoc .scroll-cue::after{content:"";display:block;width:1px;height:32px;background:linear-gradient(#9fb0c9,transparent);margin:10px auto 0;}

.bc-assoc .section{padding:110px 6%;max-width:1080px;margin:0 auto;}
.bc-assoc .section-tight{max-width:900px;margin:0 auto;padding:130px 6%;}
.bc-assoc .kicker{text-align:center;color:var(--gold-deep);font-size:.76rem;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;margin-bottom:16px;}
.bc-assoc .section h2{font-size:2.2rem;text-align:center;margin-bottom:16px;font-weight:600;}
.bc-assoc .section p.lead{text-align:center;color:var(--gray);font-size:1.06rem;max-width:600px;margin:0 auto 60px;}

.bc-assoc .perk-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:22px;}
.bc-assoc .perk-card{background:#fff;border:1px solid var(--border);border-radius:14px;padding:38px 30px;box-shadow:0 6px 20px rgba(12,31,58,.05);transition:transform .25s ease, box-shadow .25s ease;}
.bc-assoc .perk-card:hover{transform:translateY(-5px);box-shadow:0 18px 36px rgba(12,31,58,.1);}
.bc-assoc .perk-icon{width:44px;height:44px;border-radius:50%;border:1px solid var(--gold);display:flex;align-items:center;justify-content:center;color:var(--gold-deep);margin-bottom:22px;}
.bc-assoc .perk-icon svg{width:21px;height:21px;}
.bc-assoc .perk-card h3{font-size:1.05rem;font-weight:600;margin-bottom:10px;letter-spacing:.1px;}
.bc-assoc .perk-card p{font-size:.9rem;color:var(--gray);}

.bc-assoc .statement{background:var(--navy);color:#fff;text-align:center;padding:130px 6%;position:relative;overflow:hidden;}
.bc-assoc .statement::before{content:"";position:absolute;inset:0;background:radial-gradient(circle at 20% 30%,rgba(201,162,74,.12),transparent 55%);}
.bc-assoc .statement-inner{position:relative;z-index:1;}
.bc-assoc .statement .bar{width:44px;height:2px;background:var(--gold);margin:0 auto 30px;}
.bc-assoc .statement p{font-family:'Fraunces',serif;font-size:1.85rem;font-weight:500;max-width:740px;margin:0 auto;line-height:1.5;}
.bc-assoc .statement span{display:block;margin-top:24px;color:var(--gold);font-size:.78rem;letter-spacing:2.5px;text-transform:uppercase;font-weight:700;}

.bc-assoc .cta-final{text-align:center;}
.bc-assoc .cta-final h2{font-size:2.3rem;margin-bottom:16px;font-weight:600;}
.bc-assoc .cta-final p.lead{margin-bottom:42px;}
.bc-assoc .invite-note{color:var(--gray);font-size:.95rem;font-weight:600;}

.bc-assoc footer{text-align:center;padding:34px 6%;font-size:.85rem;color:var(--gray);}

@media(max-width:800px){
  .bc-assoc .perk-grid{grid-template-columns:1fr;}
  .bc-assoc .hero h1{font-size:2.3rem;}
  .bc-assoc .statement p{font-size:1.35rem;}
}
`;

function useGoogleFonts() {
  useEffect(() => {
    const links = [
      Object.assign(document.createElement("link"), { rel: "preconnect", href: "https://fonts.googleapis.com" }),
      Object.assign(document.createElement("link"), { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" }),
      Object.assign(document.createElement("link"), {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&display=swap",
      }),
    ];
    links.forEach(l => document.head.appendChild(l));
    return () => { links.forEach(l => l.remove()); };
  }, []);
}

function useRevealOnScroll(containerRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const targets = root.querySelectorAll(".reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("show"); });
    }, { threshold: 0.12 });
    targets.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [containerRef]);
}

export default function AssociateLanding() {
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const urlRef = params.get("ref") ?? "";
  const refCode = resolveReferralCode(urlRef);
  const containerRef = useRef<HTMLDivElement>(null);

  useGoogleFonts();
  useRevealOnScroll(containerRef);

  useEffect(() => {
    if (urlRef) setReferralCookie(urlRef);
  }, [urlRef]);

  const repQuery = trpc.rep.getByCode.useQuery(
    { repCode: refCode },
    { enabled: !!refCode, retry: false }
  );

  const validReferrer = isCreditableReferrer(repQuery.data as { status?: string } | null | undefined);
  const referrerName = validReferrer ? `${(repQuery.data as any).firstName} ${(repQuery.data as any).lastName}` : null;
  // Only forward a repCode into registration once it's been verified as a
  // real, active associate — never the raw, unverified query-string value.
  const getStartedHref = buildJoinAgentHref(refCode, validReferrer);

  // Admin-editable content (logo, hero copy, perk text) — Admin Portal ->
  // Associates Landing Page. Every field falls back to the page's own
  // hardcoded default whenever no override has been saved, so this query
  // (or the app_settings row it reads) being empty/unavailable never breaks
  // the public page.
  const contentQuery = trpc.associateLanding.getContent.useQuery();
  const content = contentQuery.data;
  const logoUrl = content?.logoUrl || DEFAULT_LOGO_URL;
  const heroHeadline = content?.heroHeadline || DEFAULT_HERO_HEADLINE;
  const heroSubtext = content?.heroSubtext || DEFAULT_HERO_SUBTEXT;
  const perks =
    content?.perks && content.perks.length === PERK_ICONS.length
      ? content.perks
      : DEFAULT_PERKS;

  return (
    <div className="bc-assoc" ref={containerRef}>
      <style>{STYLES}</style>

      <header>
        <img className="logo" src={logoUrl} alt="BizConnect360" />
        <a href="#start" className="btn-ghost">Get Started</a>
      </header>

      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-scrim" />
        <div className="hero-inner">
          <div className="content">
            <div className="eyebrow">BizConnect360</div>
            {referrerName && (
              <p className="invited-by">Invited by <b>{referrerName}</b></p>
            )}
            <h1>{heroHeadline}</h1>
            <p>{heroSubtext}</p>
            {getStartedHref ? (
              <Link href={getStartedHref} className="btn">Get Started</Link>
            ) : (
              <p className="invited-by">
                This link needs a recruiter invite to continue. Ask your BizConnect360 contact for their invite link.
              </p>
            )}
          </div>
        </div>
        <div className="scroll-cue">Scroll</div>
      </section>

      <section className="section reveal" id="perks">
        <div className="kicker">What You Get</div>
        <h2>Everything you need to build something real</h2>
        <p className="lead">This isn't a vague idea — it's a real, working platform designed so you can start moving from day one.</p>
        <div className="perk-grid">
          {perks.map((perk, i) => (
            <div className="perk-card" key={i}>
              <div className="perk-icon" aria-hidden="true">{PERK_ICONS[i]}</div>
              <h3>{perk.title}</h3>
              <p>{perk.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="statement reveal">
        <div className="statement-inner">
          <div className="bar" />
          <p>Every entrepreneur deserves the infrastructure to build something real — without having to build it alone.</p>
          <span>BizConnect360</span>
        </div>
      </section>

      <section className="section-tight cta-final reveal" id="start">
        <div className="kicker">Ready When You Are</div>
        <h2>Build more than a business. Build a future.</h2>
        <p className="lead">More freedom. More opportunity. More control over your future. It all begins with one decision.</p>
        {getStartedHref ? (
          <Link href={getStartedHref} className="btn">Become a BizConnect360 Associate</Link>
        ) : (
          <p className="invite-note">
            Registration requires a recruiter invite link. Ask your BizConnect360 contact for theirs.
          </p>
        )}
      </section>

      <footer>
        Copyright © 2014-2026 BizConnect360. All rights reserved.
      </footer>
    </div>
  );
}
